import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import {
  CheckCircle, Circle, CaretDown, CaretUp, X, Rocket, ArrowRight
} from "@phosphor-icons/react";

export default function OnboardingWidget() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    API.get("/tenant/onboarding-progress")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await API.post("/tenant/onboarding-dismiss");
      setData(null);
    } catch {}
    finally { setDismissing(false); }
  };

  if (loading || !data || data.dismissed || data.completed) return null;

  return (
    <div className="border border-border rounded-md bg-card mb-6 animate-fade-in overflow-hidden" data-testid="onboarding-widget">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-border bg-primary/5">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Rocket size={16} className="text-primary" weight="duotone" />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-[Chivo]" data-testid="onboarding-title">{t("onboarding.title")}</h3>
            <p className="text-[10px] text-muted-foreground">{t("onboarding.subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-bold font-mono text-primary" data-testid="onboarding-progress-text">{data.done_count}/{data.total}</span>
          <button onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-muted rounded transition-colors" data-testid="onboarding-expand-toggle">
            {expanded ? <CaretUp size={14} className="text-muted-foreground" /> : <CaretDown size={14} className="text-muted-foreground" />}
          </button>
          <button
            onClick={handleDismiss}
            disabled={dismissing}
            className="p-1 hover:bg-muted rounded transition-colors text-muted-foreground hover:text-foreground"
            title={t("onboarding.dismiss")}
            data-testid="onboarding-dismiss-btn"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 py-2.5">
        <Progress value={data.progress} className="h-2" data-testid="onboarding-progress-bar" />
        <p className="text-[10px] text-muted-foreground mt-1">{data.progress}% complete</p>
      </div>

      {/* Checklist items */}
      {expanded && (
        <div className="divide-y divide-border/50" data-testid="onboarding-checklist">
          {data.items.map((item) => (
            <Link
              key={item.id}
              to={item.link}
              className={`flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/30 group ${item.done ? "opacity-60" : ""}`}
              data-testid={`onboarding-item-${item.id}`}
            >
              {item.done ? (
                <CheckCircle size={18} className="text-emerald-500 shrink-0" weight="fill" />
              ) : (
                <Circle size={18} className="text-muted-foreground/40 shrink-0 group-hover:text-primary transition-colors" />
              )}
              <span className={`text-xs flex-1 ${item.done ? "line-through text-muted-foreground" : "font-medium"}`}>
                {item.label}
              </span>
              {!item.done && (
                <ArrowRight size={12} className="text-muted-foreground/30 group-hover:text-primary transition-colors shrink-0" />
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
